import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const MatchNotification = ({ match, onClose, onStartChat, onViewProfile }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (match) {
      setIsVisible(true);
    }
  }, [match]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleStartChat = () => {
    onStartChat(match);
    handleClose();
  };

  const handleViewProfile = () => {
    onViewProfile(match);
    handleClose();
  };

  if (!match) return null;

  return (
    <div className={`fixed inset-0 z-modal bg-background/80 backdrop-blur-cosmic transition-all duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0'
    }`}>
      <div className={`absolute inset-0 flex items-center justify-center p-4 transition-all duration-500 ${
        isVisible ? 'scale-100' : 'scale-95'
      }`}>
        <div className="w-full max-w-md bg-surface rounded-2xl shadow-elevation-5 overflow-hidden">
          {/* Celebration Header */}
          <div className="relative bg-gradient-to-br from-primary to-secondary p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 animate-cosmic-glow"></div>
            
            {/* Sparkle Animation */}
            <div className="absolute top-4 left-4 animate-gentle-pulse">
              <Icon name="Sparkles" size={24} color="white" />
            </div>
            <div className="absolute top-6 right-6 animate-gentle-pulse" style={{ animationDelay: '0.5s' }}>
              <Icon name="Star" size={20} color="white" />
            </div>
            <div className="absolute bottom-4 left-6 animate-gentle-pulse" style={{ animationDelay: '1s' }}>
              <Icon name="Zap" size={18} color="white" />
            </div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-gentle-pulse">
                <Icon name="Heart" size={32} color="white" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                It's a Match!
              </h2>
              <p className="text-white/80 text-sm">
                The stars have aligned for you and {match.name}
              </p>
            </div>
          </div>

          {/* Match Details */}
          <div className="p-6">
            {/* Profile Images */}
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-primary/30">
                  <Image
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
                    alt="Your profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-surface flex items-center justify-center">
                  <Icon name="Check" size={12} color="white" />
                </div>
              </div>
              
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center animate-gentle-pulse">
                  <Icon name="Heart" size={20} color="white" />
                </div>
              </div>
              
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-secondary/30">
                  <Image
                    src={match.profileImage}
                    alt={`${match.name}'s profile`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-surface flex items-center justify-center">
                  <Icon name="Check" size={12} color="white" />
                </div>
              </div>
            </div>

            {/* Compatibility Info */}
            <div className="glass bg-surface/30 rounded-lg p-4 mb-6">
              <div className="text-center mb-3">
                <div className="text-2xl font-heading font-bold text-success mb-1">
                  {match.compatibilityScore}%
                </div>
                <div className="text-sm text-text-secondary">
                  Astrological Compatibility
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-lg mb-1">{match.astrology.sun.symbol}</div>
                  <div className="text-xs text-text-secondary">Sun</div>
                </div>
                <div>
                  <div className="text-lg mb-1">{match.astrology.moon.symbol}</div>
                  <div className="text-xs text-text-secondary">Moon</div>
                </div>
                <div>
                  <div className="text-lg mb-1">{match.astrology.rising.symbol}</div>
                  <div className="text-xs text-text-secondary">Rising</div>
                </div>
              </div>
            </div>

            {/* Musical Harmony */}
            <div className="glass bg-surface/30 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Music" size={16} color="#8B5CF6" />
                  <span className="text-sm font-medium text-text-primary">Musical Harmony</span>
                </div>
                <div className="text-sm font-bold text-accent">
                  {match.musicalCompatibility}%
                </div>
              </div>
              <div className="text-xs text-text-secondary">
                Your tones create beautiful harmonic resonance in {match.harmonicKey}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                variant="primary"
                iconName="MessageCircle"
                onClick={handleStartChat}
                fullWidth
              >
                Start Conversation
              </Button>
              
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="ghost"
                  iconName="User"
                  onClick={handleViewProfile}
                >
                  View Profile
                </Button>
                <Button
                  variant="ghost"
                  iconName="X"
                  onClick={handleClose}
                >
                  Keep Swiping
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchNotification;